import { useEffect } from 'react';
import type { VerticalLine } from '@/modules/shared/DataLayers/types';

export interface UseAutoBenchmarkProps {
  verticalLines: VerticalLine[];
  sectionConfigGraphs: any[];
  setBenchmarkLineBySection: React.Dispatch<React.SetStateAction<Record<string, string | null>>>;
}

/**
 * Automatically sets the first user marker as benchmark across all sections
 * Only triggers when there's exactly 1 user marker
 */
export function useAutoBenchmark({
  verticalLines,
  sectionConfigGraphs,
  setBenchmarkLineBySection,
}: UseAutoBenchmarkProps): void {
  useEffect(() => {
    const userMarkers = verticalLines.filter((line: any) => !line.isHarmonic && line.markerType === 'user');

    // Only auto-set if we have exactly 1 user marker
    if (userMarkers.length === 1) {
      const firstMarker = userMarkers[0];
      const allSectionKeys = sectionConfigGraphs.map((g: any) => g.id);

      // Set this marker as benchmark for all sections
      // Since markers are now global (no sectionKey), the same marker is visible to all sections
      // Using functional setState, so we don't need benchmarkLineBySection in dependencies
      setBenchmarkLineBySection((prev) => {
        // Check if any section already has a benchmark set
        const hasAnyBenchmark = allSectionKeys.some((key: string) => prev[key] !== null && prev[key] !== undefined);

        // If no section has a benchmark yet, set the first marker for all sections
        if (!hasAnyBenchmark) {
          const newState = { ...prev };
          allSectionKeys.forEach((key: string) => {
            // Set the same marker ID for all sections
            newState[key] = firstMarker.id;
          });
          return newState;
        }

        return prev; // No changes needed
      });
    }
  }, [verticalLines, sectionConfigGraphs, setBenchmarkLineBySection]);
}
